<template>
  <div
    v-if="visible"
    ref="indicatorRef"
    class="adv-menu__indicator"
    :class="{ '--slider': mode === 'slider' }"
    :style="indicatorStyle"
  />
</template>

<script setup lang="ts">
import { computed, CSSProperties, ref, watch } from 'vue';
import type { MenuOverflowMode } from './types';

interface Props {
  visible: boolean;
  left: number;
  width: number;
  color: string;
  mode: MenuOverflowMode;
  animationDuration: number;
  timingFunction: string;
  paddingY?: string;
  gpuAcceleration?: boolean;
  willChange?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  paddingY: '0px',
  gpuAcceleration: true,
  willChange: true,
});

const indicatorRef = ref<HTMLElement | null>(null);

// Computed style for indicator
const indicatorStyle = computed<CSSProperties>(() => {
  const paddingValue = props.paddingY || '0px';
  const topBottom = props.mode === 'slider'
    ? (paddingValue === '0px' ? '4px' : `calc(${paddingValue} + 4px)`)
    : '0px';

  const baseStyle: CSSProperties = {
    backgroundColor: props.color,
    transitionDuration: `${props.animationDuration}ms`,
    transitionTimingFunction: props.timingFunction,
  };

  if (props.mode === 'slider') {
    return {
      ...baseStyle,
      left: `${props.left}px`,
      width: `${props.width}px`,
      top: topBottom,
      bottom: topBottom,
      transitionProperty: 'left, width, background-color',
    };
  } else {
    return {
      ...baseStyle,
      transform: `translateX(${props.left}px)`,
      width: `${props.width}px`,
      top: topBottom,
      bottom: topBottom,
      transitionProperty: 'transform, width, background-color',
    };
  }
});

// Expose ref for parent component
defineExpose({
  indicatorRef,
});
</script>

<style lang="scss" scoped>
.adv-menu__indicator {
  position: absolute;
  left: 0;
  border-radius: 8px;
  pointer-events: none;
  z-index: 1;
  transform: translateZ(0);
  contain: strict;
  isolation: isolate;
  backface-visibility: hidden;

  &.--slider {
    position: absolute;
    top: 4px;
    bottom: 4px;
  }
}
</style>
